import { useState } from "react";
import { toast } from "sonner";
import { FadeUp, MaskedLine, SectionTag } from "../components/Reveal";
import { CLINIC, SERVICES } from "../data/clinic";
import { Phone, MapPin, Clock, Send, Mail } from "lucide-react";

const initial = { name: "", phone: "", email: "", service: "", date: "", notes: "" };

const fieldCls =
    "w-full rounded-xl border border-emerald-500/20 bg-[#0b150e]/80 px-4 py-3.5 text-sm font-light text-emerald-50 placeholder:text-[#687f6e] outline-none transition-all duration-300 focus:border-emerald-400/70 focus:shadow-[0_0_20px_rgba(34,197,94,0.12)]";

const Contact = () => {
    const [form, setForm] = useState(initial);
    const [errors, setErrors] = useState({});

    const set = (k) => (e) => {
        setForm({ ...form, [k]: e.target.value });
        setErrors({ ...errors, [k]: undefined });
    };

    const submit = (e) => {
        e.preventDefault();
        const errs = {};
        if (!form.name.trim()) errs.name = "Please enter your name";
        if (!/^[\d\s+-]{10,}$/.test(form.phone.trim())) errs.phone = "Enter a valid phone number";
        if (form.email && !/^\S+@\S+\.\S+$/.test(form.email)) errs.email = "Enter a valid email";
        if (!form.service) errs.service = "Please choose a service";
        setErrors(errs);
        if (Object.keys(errs).length) {
            toast.error("Please fix the highlighted fields.");
            return;
        }
        toast.success(
            `Thank you, ${form.name.split(" ")[0]}! Your request is noted — for instant confirmation, call us at ${CLINIC.phoneDisplay}.`
        );
        setForm(initial);
    };

    return (
        <main data-testid="contact-page" className="pt-32">
            <section className="relative mx-auto max-w-7xl overflow-hidden px-4 pb-24 sm:px-8 lg:px-16 lg:pb-32">
                <div className="noise-overlay pointer-events-none absolute inset-0 opacity-20" />
                <SectionTag className="relative mb-8">Contact Us</SectionTag>
                <h1 className="font-display relative max-w-4xl text-4xl font-light leading-[1.05] tracking-tight sm:text-6xl lg:text-7xl">
                    <MaskedLine>Let's get your</MaskedLine>
                    <MaskedLine delay={0.12}>
                        vision <em className="text-emerald-400">checked</em>.
                    </MaskedLine>
                </h1>

                <div className="relative mt-16 grid gap-12 lg:grid-cols-12 lg:gap-16">
                    <FadeUp className="lg:col-span-7">
                        <form
                            data-testid="contact-form"
                            onSubmit={submit}
                            noValidate
                            className="rounded-3xl border border-emerald-500/20 bg-[#101f15]/60 p-8 backdrop-blur-sm lg:p-10"
                        >
                            <p className="mb-8 text-xs font-mono tracking-[0.25em] uppercase text-emerald-400">
                                Request an appointment
                            </p>
                            <div className="grid gap-6 sm:grid-cols-2">
                                <div>
                                    <input
                                        data-testid="input-full-name"
                                        className={fieldCls}
                                        placeholder="Full name *"
                                        value={form.name}
                                        onChange={set("name")}
                                    />
                                    {errors.name && <p className="mt-2 text-xs text-red-400">{errors.name}</p>}
                                </div>
                                <div>
                                    <input
                                        data-testid="input-phone-number"
                                        className={fieldCls}
                                        placeholder="Phone number *"
                                        value={form.phone}
                                        onChange={set("phone")}
                                    />
                                    {errors.phone && <p className="mt-2 text-xs text-red-400">{errors.phone}</p>}
                                </div>
                                <div>
                                    <input
                                        data-testid="input-email"
                                        className={fieldCls}
                                        placeholder="Email (optional)"
                                        value={form.email}
                                        onChange={set("email")}
                                    />
                                    {errors.email && <p className="mt-2 text-xs text-red-400">{errors.email}</p>}
                                </div>
                                <div>
                                    <input
                                        data-testid="input-preferred-date"
                                        type="date"
                                        className={`${fieldCls} [color-scheme:dark]`}
                                        value={form.date}
                                        onChange={set("date")}
                                    />
                                </div>
                                <div className="sm:col-span-2">
                                    <select
                                        data-testid="select-service-type"
                                        className={`${fieldCls} ${form.service ? "" : "text-[#687f6e]"}`}
                                        value={form.service}
                                        onChange={set("service")}
                                    >
                                        <option value="" disabled>
                                            Choose a service *
                                        </option>
                                        {SERVICES.map((s) => (
                                            <option key={s.title} value={s.title}>
                                                {s.title}
                                            </option>
                                        ))}
                                        <option value="Other">Something else</option>
                                    </select>
                                    {errors.service && (
                                        <p className="mt-2 text-xs text-red-400">{errors.service}</p>
                                    )}
                                </div>
                                <div className="sm:col-span-2">
                                    <textarea
                                        data-testid="textarea-notes"
                                        rows={4}
                                        className={`${fieldCls} resize-none`}
                                        placeholder="Anything we should know? Symptoms, concerns, insurance questions…"
                                        value={form.notes}
                                        onChange={set("notes")}
                                    />
                                </div>
                            </div>
                            <button
                                type="submit"
                                data-testid="contact-form-submit-button"
                                className="group mt-8 flex w-full items-center justify-center gap-3 rounded-full bg-emerald-500 py-4 text-sm font-semibold text-[#04120a] transition-all duration-300 hover:bg-emerald-400 hover:shadow-[0_0_36px_rgba(34,197,94,0.45)] sm:w-auto sm:px-10"
                            >
                                <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                                Send Request
                            </button>
                            <p className="mt-5 text-xs font-light text-[#687f6e]">
                                Prefer to talk? Call us directly at{" "}
                                <a href={CLINIC.phoneHref} className="text-emerald-300 underline-offset-4 hover:underline">
                                    {CLINIC.phoneDisplay}
                                </a>
                            </p>
                        </form>
                    </FadeUp>

                    <div className="space-y-6 lg:col-span-5">
                        <FadeUp delay={0.1}>
                            <div data-testid="clinic-address-card" className="flex gap-4 rounded-2xl border border-emerald-500/15 bg-[#101f15]/60 p-6">
                                <MapPin className="h-6 w-6 shrink-0 text-emerald-400" />
                                <div>
                                    <p className="mb-1.5 text-xs font-mono tracking-[0.25em] uppercase text-emerald-400">Address</p>
                                    <p className="text-sm font-light leading-relaxed text-[#a3b8a8]">{CLINIC.address}</p>
                                    <p className="mt-2 text-xs text-[#687f6e]">Near Shakti Sangha Club</p>
                                </div>
                            </div>
                        </FadeUp>
                        <FadeUp delay={0.15}>
                            <div
                                data-testid="clinic-phone-card"
                                className="flex gap-4 rounded-2xl border border-emerald-500/15 bg-[#101f15]/60 p-6"
                            >
                                <Phone className="h-6 w-6 shrink-0 text-emerald-400" />
                                <div>
                                    <p className="mb-1.5 text-xs font-mono tracking-[0.25em] uppercase text-emerald-400">Phone</p>
                                    <a
                                        href={CLINIC.phoneHref}
                                        className="block text-2xl font-light text-emerald-50 transition-colors duration-300 hover:text-emerald-300"
                                    >
                                        {CLINIC.phoneDisplay}
                                    </a>
                                    <a
                                        href={CLINIC.phoneHref2}
                                        data-testid="clinic-phone-card-landline"
                                        className="mt-1 block text-sm font-light text-[#a3b8a8] transition-colors duration-300 hover:text-emerald-300"
                                    >
                                        {CLINIC.phoneDisplay2}
                                    </a>
                                </div>
                            </div>
                        </FadeUp>
                        <FadeUp delay={0.18}>
                            <a
                                href={CLINIC.emailHref}
                                data-testid="clinic-email-card"
                                className="group flex gap-4 rounded-2xl border border-emerald-500/15 bg-[#101f15]/60 p-6 transition-all duration-300 hover:border-emerald-400/50"
                            >
                                <Mail className="h-6 w-6 shrink-0 text-emerald-400 transition-transform duration-300 group-hover:rotate-12" />
                                <div>
                                    <p className="mb-1.5 text-xs font-mono tracking-[0.25em] uppercase text-emerald-400">Mail us</p>
                                    <p className="text-lg font-light text-emerald-50 group-hover:text-emerald-300">
                                        {CLINIC.email}
                                    </p>
                                </div>
                            </a>
                        </FadeUp>
                        <FadeUp delay={0.2}>
                            <div data-testid="clinic-hours-card" className="flex gap-4 rounded-2xl border border-emerald-500/15 bg-[#101f15]/60 p-6">
                                <Clock className="h-6 w-6 shrink-0 text-emerald-400" />
                                <div>
                                    <p className="mb-1.5 text-xs font-mono tracking-[0.25em] uppercase text-emerald-400">Hours</p>
                                    <p className="text-sm font-light text-[#a3b8a8]">{CLINIC.hours}</p>
                                    <p className="text-sm font-light text-[#687f6e]">{CLINIC.sunday}</p>
                                </div>
                            </div>
                        </FadeUp>
                        <FadeUp delay={0.25}>
                            <div
                                data-testid="clinic-map-panel"
                                className="overflow-hidden rounded-2xl border border-emerald-500/20"
                            >
                                <iframe
                                    title="Optiplus location map"
                                    src={CLINIC.mapEmbed}
                                    className="map-dark h-64 w-full border-0"
                                    loading="lazy"
                                    referrerPolicy="no-referrer-when-downgrade"
                                />
                            </div>
                        </FadeUp>
                    </div>
                </div>
            </section>
        </main>
    );
};

export default Contact;
